import type { ThemeConfig } from "antd";
import { theme as antdAlgorithm } from "antd";
import type { ThemeId } from "./theme/theme";
import { THEMES } from "./theme/theme";

/** Read a CSS custom property from the document root (falls back when unset). */
function cssVar(name: string, fallback: string): string {
  if (typeof document === "undefined") return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}

const DARK_ACCENT_OVERRIDES: Partial<Record<ThemeId, string>> = {
  blue: "#60a5fa",
  violet: "#a78bfa",
  emerald: "#34d399",
  rose: "#fb7185",
  zinc: "#e4e4e7",
};

export type BuildAntdThemeOptions = {
  themeId?: ThemeId;
  resolvedMode?: "light" | "dark";
  fontFamily?: string;
};

/**
 * Build Ant Design theme tokens from Cenna appearance settings so accent / mode
 * changes in the shell profile menu flow through to legacy Ant panels.
 */
export function buildAntdTheme(options: BuildAntdThemeOptions = {}): ThemeConfig {
  const themeId = options.themeId ?? "zinc";
  const isDark = options.resolvedMode === "dark";
  const themeOption = THEMES.find((t) => t.id === themeId);
  const accent =
    (isDark ? DARK_ACCENT_OVERRIDES[themeId] : undefined) ||
    themeOption?.accent ||
    cssVar("--accent", "#3f3f46");
  const fontSans = options.fontFamily || cssVar("--font-sans", "Inter");

  return {
    algorithm: isDark ? antdAlgorithm.darkAlgorithm : antdAlgorithm.defaultAlgorithm,
    token: {
      colorPrimary: accent,
      borderRadius: 8,
      fontFamily: `${fontSans}, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`,
      colorBgContainer: isDark ? "#18181b" : "#ffffff",
      colorBgLayout: isDark ? "#101012" : "#ffffff",
      colorBorder: isDark ? "#2a2a2e" : "#e4e4e7",
      colorText: isDark ? "#e4e4e7" : "#3f3f46",
      colorTextSecondary: isDark ? "#a1a1aa" : "#71717a",
    },
    components: {
      Tabs: { titleFontSizeSM: 14 },
      Card: { paddingLG: 16 },
    },
  };
}

/** Static fallback used at module load (before ThemeProvider applies attrs). */
export const antdTheme: ThemeConfig = {
  token: {
    colorPrimary: "#3f3f46",
    borderRadius: 8,
    fontFamily:
      'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  components: {
    Tabs: { titleFontSizeSM: 14 },
    Card: { paddingLG: 16 },
  },
};
